import { Component, computed, effect, inject, signal } from '@angular/core';
import { TableModule } from '@openng/optimus-ui/table';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { isAllowed, routeParams, translate } from '../../../../../modules/shared/shared.global-service';
import { TableColumn, TableColumnType } from '../../../../../modules/shared/components/kit/table/table.types';
import { dateField, doctorChipField, textColumn } from '../../../../../modules/shared/components/kit/table/table.fields';
import { ButtonComponent } from '../../../../kit/button/button.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationWrapperService } from '../../../../../services/wrappers/confirmation-wrapper.service';
import { FolderEPrescriptionsPageStore } from './folder-e-prescriptions-page.store';
import { contentGlobalSuccess } from '../../../../../modules/content/content.error';
import { EPrescriptionObject } from '@libs/types/src/services/object-type/sukl/e-prescription.object';
import { LinkButtonComponent } from '../../../../kit/button/link-button.component';
import { workUri } from '../../../../../utils/router.utils';
import { FolderEPrescriptionsLoadCertificateModalComponent } from '../folder-e-prescriptions-load-certificate-modal/folder-e-prescriptions-load-certificate-modal.component';
import { BaseDialogService } from '../../../../kit/base-dialog/base-dialog-service';
import { TableComponent } from '../../../../../modules/shared/components/kit/table/table.component';
import { MedicationInfoComponent } from '../../../../../modules/shared/components/kit/medication-info/medication-info.component';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';
import { ShortDatePipe } from '../../../../../modules/shared/pipes/short-date.pipe';
import { folderEPrescriptionsPageTableFrag } from './folder-e-prescriptions-page.query';
import { BackgroundTaskLaunchService } from '../../../../../modules/layout/store/background-tasks/background-task-launch.service';
import {
    BackgroundActionType,
    ClientDocumentExportKind,
} from '@libs/types/src/notifications/types/background-action.types';

@Component({
    selector: 'app-folder-e-prescriptions-page',
    templateUrl: 'folder-e-prescriptions-page.component.html',
    styleUrls: ['folder-e-prescriptions-page.component.scss'],
    providers: [FolderEPrescriptionsPageStore],
    imports: [
        TableModule,
        PrimeTemplate,
        ButtonComponent,
        LinkButtonComponent,
        TableComponent,
        MedicationInfoComponent,
        ShortDatePipe,
    ],
})
export class FolderEPrescriptionsPageComponent {
    public readonly dialogService = inject(BaseDialogService);
    public readonly confirmationService = inject(ConfirmationWrapperService);
    public readonly router = inject(Router);
    public readonly route = inject(ActivatedRoute);
    public readonly store = inject(FolderEPrescriptionsPageStore);
    protected readonly folderStore = inject(FolderGlobalStore);
    private readonly backgroundTaskLauncher = inject(
        BackgroundTaskLaunchService,
    );

    protected readonly prefix = 'ePrescriptions.';
    protected additionalFilter = computed(() => {
        return [
            [
                {
                    column: 'folderId',
                    operator: 'EQ',
                    value: [routeParams.folderId()],
                },
            ],
        ];
    });

    protected columns: TableColumn[] = [
        { name: 'print' },
        textColumn('medications'),
        { name: 'suklId' },
        dateField('issuedAt'),
        dateField('validTo'),
        // TODO later
        // {
        //     name: 'status',
        //     type: TableColumnType.Tag,
        //     filterable: {
        //         selectOptions: () => ePrescriptionStatusEnumOptions,
        //     },
        //     tagOptions: {
        //         labelPrefix: `${this.prefix}statusEnum.`,
        //     },
        //     severity: (item: string) => {
        //         return item === EPrescriptionStatusEnum.PRESCRIBED ? 'primary' : item === EPrescriptionStatusEnum.FULL_ISSUED ? 'success' : 'warning';
        //     },
        // },
        {
            name: 'validity',
            type: TableColumnType.Tag,
            filterable: false,
            dataKey: 'validTo',
            data: (validTo: Date) => {
                const now = new Date();
                return now > new Date(validTo) ? 'PASSED' : 'VALID';
            },
            tagOptions: {
                labelPrefix: `${this.prefix}validityEnum.`,
            },
            severity: (item: string) => {
                return item === 'PASSED' ? 'danger' : 'success';
            },
        },
        doctorChipField('doctor'),
        { name: 'work' },
    ];

    protected isReady = signal(false);

    protected canCreate = computed(() => isAllowed['ePrescription.create']());

    constructor() {
        effect(async () => {
            await this.checkReadiness();
        });
    }

    protected create(_event: any) {
        return this.router.navigate(['./create'], { relativeTo: this.route });
    }

    /**
     * TODO bude se dělat detail page?
     */
    protected detail(event: any) {
        return this.router.navigate([`./${event.id}`], {
            relativeTo: this.route,
        });
    }

    protected async print(item: EPrescriptionObject) {
        await this.backgroundTaskLauncher.start({
            type: BackgroundActionType.CLIENT_DOCUMENT_EXPORT,
            payload: {
                folderId: Number(routeParams.folderId()),
                kind: ClientDocumentExportKind.E_PRESCRIPTION,
                input: { id: item.id },
            },
            alreadyRunningMessage:
                'person.documentExport.backgroundTask.messages.alreadyRunning',
            failedMessage:
                'person.documentExport.backgroundTask.messages.startFailed',
        });
    }

    /**
     * TODO má smysl mazat? Nelze změnit v SUKL
     */
    protected async delete(event: any): Promise<void> {
        // TODO Losos ePrescription asi nelze mazat?
        if (await this.confirmationService.delete(event.target)) {
            await this.store.api.deleteEPrescription(event.item.id);
            contentGlobalSuccess.next(`${this.prefix}message.deleted`);
        }
    }

    protected async checkReadiness() {
        if (!this.folderStore.canEditRecord()) return;

        if (this.store.folder()?.serviceId && this.store.currentUser()?.id) {
            const res = await this.store.api.checkEPrescriptionReadiness({
                folderId: this.store.folder()!.id,
                userId: this.store.currentUser()!.id,
            });
            if (!res.ready) {
                const ref = this.dialogService.open(
                    FolderEPrescriptionsLoadCertificateModalComponent,
                    {
                        header: translate.instant(
                            'ePrescriptions.checkReadiness.title',
                        ),
                        styleClass: 'p-dialog-width-60',
                        data: { readiness: res },
                    },
                );
                ref.onClose.subscribe(async (result) => {
                    if (result) {
                        this.isReady.set(result);
                    }
                });
            } else {
                this.isReady.set(true);
            }
        }
    }

    protected async goToWork(work: any): Promise<void> {
        await this.router.navigateByUrl(workUri(work, work.serviceId));
    }

    protected readonly isAllowed = isAllowed;
    protected readonly tableFragment = folderEPrescriptionsPageTableFrag;
}
